<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { readBootstrap } from '../../shared/bootstrap.js';
import { getOpeningHoursState } from '../../shared/opening-hours.js';
import BtwToggle from './BtwToggle.vue';
import { useBtwToggle } from './useBtwToggle.js';
import HeaderSearch from './HeaderSearch.vue';
import OpeningHoursPanel from './OpeningHoursPanel.vue';
import HeaderMegaMenu from './HeaderMegaMenu.vue';

/** Fallback als bootstrap geen logoSrc heeft */
const LOGO_FALLBACK = '/image/VSB-Verhuur-Border-SVG.svg';

/** Tijdelijk uit — zet op true wanneer klaar voor live */
const showKlantenservice = false;
const showReserveerlijst = false;

const boot = readBootstrap(
  typeof document !== 'undefined' ? document.getElementById('vsb-vue-site-header') : null,
  'vsb-site-header-bootstrap'
);

const home = computed(() => boot.home || '/');
const logoSrc = computed(() => boot.logoSrc || LOGO_FALLBACK);
const logoAlt = computed(() => boot.logoAlt || 'VSB Verhuur');
const searchPlaceholder = computed(
  () => boot.searchPlaceholder || 'Zoek in meer dan 4000 producten…'
);
const showCategoryButton = computed(() => boot.showCategoryButton !== false);
const showAnnouncementBar = computed(
  () => typeof boot.announcement === 'string' && boot.announcement.trim() !== ''
);
const isExcl = boot.isExcl !== false;
const { isPrivate, onToggle: onBtwToggle } = useBtwToggle(isExcl, boot);
const searchQuery = computed(() => (typeof boot.search === 'string' ? boot.search : ''));
const cartHref = computed(() => boot.cartHref || '/index.php?route=checkout/cart');
const cartCount = computed(() => Number(boot.cartCount) || 0);

const announcement = computed(() =>
  typeof boot.announcement === 'string'
    ? boot.announcement
    : 'Nieuwe bussen in de verhuur!'
);
const announcementHref = computed(() => boot.announcementHref || 'https://vsbverhuur.nl/nieuw-aanbod');
const announcementOpensNewTab = computed(() => {
  const href = String(announcementHref.value || '');
  if (!/^https?:\/\//i.test(href)) {
    return false;
  }
  try {
    return typeof window !== 'undefined' && new URL(href).hostname !== window.location.hostname;
  } catch {
    return true;
  }
});
const phone = computed(() => boot.phone || '0345 – 502615');
const phoneTel = computed(() => boot.phoneTel || '0345502615');
const email = computed(() => boot.email || 'info@vsbverhuur.nl');

const hours = computed(() => {
  if (boot.openingHours && typeof boot.openingHours === 'object') {
    return boot.openingHours;
  }
  return getOpeningHoursState();
});

const holidayBanner = computed(() => boot.holidayBanner || { show: false, message: '' });

const categories = computed(() => (Array.isArray(boot.categories) ? boot.categories : []));
const serviceLinks = computed(() => (Array.isArray(boot.serviceMenu) ? boot.serviceMenu : []));
const overLinks = computed(() => (Array.isArray(boot.overMenu) ? boot.overMenu : []));

const activePanel = ref(null);

const navItems = computed(() =>
  Array.isArray(boot.nav) && boot.nav.length
    ? boot.nav
    : [
        { label: 'Assortiment', dropdown: 'assortiment' },
        { label: 'Service', dropdown: 'service' },
        { label: 'Over VSB', dropdown: 'over' },
        // Tijdelijk uit: nog geen nieuwsitems, pagina is leeg
        // { label: 'Nieuws', href: '/index.php?route=information/information&information_id=33' },
        { label: 'Contact', href: '/index.php?route=information/contact' }
      ]
);

const bannerVisible = ref(false);
const stickyFixed = ref(false);
let stickyOffset = 0;

function onScroll() {
  stickyFixed.value = window.scrollY > stickyOffset;
}

onMounted(() => {
  if (
    showAnnouncementBar.value &&
    localStorage.getItem('vsb-announcement-closed') !== 'true'
  ) {
    bannerVisible.value = true;
  }

  const stickyEl = document.querySelector('.vsb-header-sticky');
  if (stickyEl) {
    stickyOffset = stickyEl.getBoundingClientRect().top + window.scrollY;
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('keydown', onDocumentKeydown);
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  document.removeEventListener('click', onDocumentClick);
  document.removeEventListener('keydown', onDocumentKeydown);
});

function onDocumentClick(e) {
  if (!activePanel.value) return;
  if (e.target.closest('.vsb-header__nav-item, .vsb-header__categories, .vsb-mega')) {
    return;
  }
  const root = document.getElementById('vsb-vue-site-header');
  if (root && !root.contains(e.target)) {
    activePanel.value = null;
  }
}

function onDocumentKeydown(e) {
  if (e.key === 'Escape') {
    activePanel.value = null;
  }
}

function closeAnnouncement() {
  bannerVisible.value = false;
  localStorage.setItem('vsb-announcement-closed', 'true');
}

function togglePanel(id) {
  activePanel.value = activePanel.value === id ? null : id;
}

function openDropdown(id) {
  togglePanel(id);
}

function openCategories() {
  togglePanel('assortiment');
}

function isNavActive(dropdown) {
  return activePanel.value === dropdown;
}

function toggleMobileNav() {
  const menu = document.getElementById('mobilemenu');
  const burger = document.querySelector('.vsb-header__burger');
  if (!menu) return;
  menu.classList.toggle('active');
  burger?.classList.toggle('active');
}
</script>

<template>
  <header class="vsb-header" data-vsb-vue="site-header" :class="{ 'vsb-header--menu-open': activePanel }">
    <div v-if="bannerVisible && showAnnouncementBar" class="vsb-header__announce">
      <div class="vsb-header__inner">
        <a
          :href="announcementHref"
          class="vsb-header__announce-link"
          :target="announcementOpensNewTab ? '_blank' : undefined"
          :rel="announcementOpensNewTab ? 'noopener noreferrer' : undefined"
        >
          <span class="vsb-header__check" aria-hidden="true">✓</span>
          {{ announcement }}
        </a>
        <button type="button" class="vsb-header__announce-close" aria-label="Melding sluiten" @click="closeAnnouncement">
          ×
        </button>
      </div>
    </div>

    <div class="vsb-header-sticky" :class="{ 'vsb-header-sticky--fixed': stickyFixed }">
      <div class="vsb-header__utility">
        <div class="vsb-header__inner vsb-header__utility-row">
          <a
            v-if="showAnnouncementBar"
            :href="announcementHref"
            class="vsb-header__utility-note vsb-header__utility-note--desktop"
            :target="announcementOpensNewTab ? '_blank' : undefined"
            :rel="announcementOpensNewTab ? 'noopener noreferrer' : undefined"
          >
            {{ announcement }}
          </a>
          <div class="vsb-header__utility-actions">
            <BtwToggle :is-private="isPrivate" @toggle="onBtwToggle" />
            <template v-if="showKlantenservice">
              <span class="vsb-header__divider hide-mobile" aria-hidden="true" />
              <a href="/index.php?route=information/information&information_id=12" class="vsb-header__link">
                Klantenservice
              </a>
            </template>
            <OpeningHoursPanel class="hide-mobile" :hours="hours" />
          </div>
        </div>
      </div>

      <div class="vsb-header__main">
        <div class="vsb-header__inner vsb-header__main-row">
          <div class="vsb-site-logo">
            <a :href="home" class="vsb-site-logo__link" :title="logoAlt">
              <img
                class="vsb-site-logo__img"
                width="300"
                height="41"
                :src="logoSrc"
                :alt="logoAlt"
                fetchpriority="high"
                loading="eager"
              />
            </a>
          </div>

          <nav class="vsb-header__nav hide-mobile" aria-label="Hoofdnavigatie">
            <ul class="vsb-header__nav-list">
              <li v-for="(item, i) in navItems" :key="i">
                <button
                  v-if="item.dropdown"
                  type="button"
                  class="vsb-header__nav-item"
                  :class="{ 'vsb-header__nav-item--active': isNavActive(item.dropdown) }"
                  :aria-expanded="isNavActive(item.dropdown) ? 'true' : 'false'"
                  @click="openDropdown(item.dropdown)"
                >
                  {{ item.label }}
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <path d="M3 4.5L6 7.5L9 4.5" fill="none" stroke="currentColor" stroke-width="1.5" />
                  </svg>
                </button>
                <a v-else :href="item.href" class="vsb-header__nav-item">{{ item.label }}</a>
              </li>
            </ul>
          </nav>

          <div class="vsb-header__contact hide-mobile">
            <a :href="`tel:${phoneTel}`" class="vsb-header__contact-block">
              <span class="vsb-header__contact-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path
                    d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"
                  />
                </svg>
              </span>
              <span>
                <strong>{{ phone }}</strong>
                <small>{{ hours.phoneHours }}</small>
              </span>
            </a>
            <a :href="`mailto:${email}`" class="vsb-header__contact-block">
              <span class="vsb-header__contact-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                  <path d="M4 4h16v16H4z" />
                  <path d="M22 6l-10 7L2 6" />
                </svg>
              </span>
              <span>
                <strong>{{ email }}</strong>
                <small>Wij streven naar een snelle reactie!</small>
              </span>
            </a>
          </div>

          <button
            type="button"
            class="vsb-header__burger show-mobile"
            aria-label="Menu"
            @click="toggleMobileNav"
          >
            <span /><span /><span />
          </button>
        </div>

        <p
          v-if="holidayBanner.show"
          class="vsb-header__holiday"
          :class="`vsb-header__holiday--${holidayBanner.variant || 'default'}`"
        >
          {{ holidayBanner.message }}
        </p>

      </div>

      <div class="vsb-header__action">
        <div class="vsb-header__inner vsb-header__action-row">
          <button
            v-if="showCategoryButton"
            type="button"
            class="vsb-header__categories hide-mobile"
            :class="{ 'vsb-header__categories--active': activePanel === 'assortiment' }"
            @click="openCategories"
          >
            <span class="vsb-header__categories-icon" aria-hidden="true">
              <span /><span /><span />
            </span>
            Alle categorieën
          </button>
          <HeaderSearch
            :initial-query="searchQuery"
            :placeholder="searchPlaceholder"
            class="vsb-header__search"
          />
          <a v-if="showReserveerlijst" :href="cartHref" class="vsb-header__cart">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
              <circle cx="9" cy="20" r="1" />
              <circle cx="18" cy="20" r="1" />
              <path d="M2 2h2l2.4 12.4a2 2 0 002 1.6h9.8a2 2 0 002-1.6L22 6H6" />
            </svg>
            <span>Reserveerlijst</span>
            <span v-if="cartCount > 0" class="vsb-header__cart-badge">{{ cartCount }}</span>
          </a>
        </div>
      </div>

      <HeaderMegaMenu
        :panel="activePanel"
        :categories="categories"
        :service-links="serviceLinks"
        :over-links="overLinks"
        @close="activePanel = null"
      />
    </div>
  </header>
</template>

<style scoped>
.vsb-header {
  position: relative;
  z-index: 1000;
  /* Mock-up kleurenpalet */
  --vsb-blue-dark: #0b3d91;
  --vsb-blue-mid: #1456b8;
  --vsb-blue-light: #eef4fb;
  --vsb-blue-subtle: #f7fafe;
  --vsb-navy: var(--vsb-blue-dark);
  --vsb-accent: var(--vsb-blue-mid);
  --vsb-top: var(--vsb-blue-light);
  --vsb-surface: var(--vsb-blue-subtle);
  --vsb-muted: #5c6f82;
  font-family: 'Source Sans Pro', 'Be Vietnam Pro', system-ui, sans-serif;
  color: var(--vsb-navy);
}

.vsb-header__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.25rem;
  box-sizing: border-box;
}

.vsb-header__announce {
  background: var(--vsb-blue-light);
  border-bottom: 1px solid #dce8f4;
  font-size: 0.8125rem;
}

.vsb-header__announce .vsb-header__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.25rem;
  position: relative;
}

.vsb-header__announce-link {
  color: var(--vsb-navy);
  text-decoration: none;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.vsb-header__check {
  color: var(--vsb-accent);
  font-weight: 700;
}

.vsb-header__announce-close {
  position: absolute;
  right: 1rem;
  border: none;
  background: none;
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  color: var(--vsb-muted);
}

.vsb-header-sticky {
  position: relative;
  background: #fff;
  transition: box-shadow 0.25s;
  overflow: visible;
}

.vsb-header-sticky--fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08);
}

.vsb-header__utility {
  background: var(--vsb-blue-light);
  border-bottom: 1px solid #dce8f4;
}

.vsb-header__utility-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 2.5rem;
  gap: 1rem;
}

.vsb-header__utility-note--desktop {
  display: none;
  margin: 0;
  flex: 1;
  font-size: 0.8125rem;
  color: var(--vsb-muted);
  text-decoration: none;
}

.vsb-header__utility-note--desktop:hover {
  color: var(--vsb-navy);
  text-decoration: underline;
}

.vsb-header__utility-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.vsb-header__divider {
  width: 1px;
  height: 1.25rem;
  background: #cbd5e1;
}

.vsb-header__link {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--vsb-navy);
  text-decoration: none;
}

.vsb-header__link:hover {
  color: var(--vsb-accent);
  text-decoration: underline;
}

.vsb-header__main {
  background: #fff;
  border-bottom: 1px solid #e8f0f8;
}

.vsb-header__main-row {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-height: 4.5rem;
}

/* Logo: vaste px (intrinsiek 300×41) — geen width:% in flex (wordt anders 0×0) */
.vsb-site-logo {
  flex: 0 0 300px;
  width: 300px;
  max-width: min(300px, 55vw);
}

.vsb-site-logo__link {
  display: block;
  width: 300px;
  max-width: 100%;
  height: 41px;
  line-height: 0;
}

.vsb-site-logo__img {
  display: block;
  width: 300px;
  height: 41px;
  max-width: 100%;
  object-fit: contain;
  object-position: left center;
}

.vsb-header__nav {
  flex: 1;
  display: flex;
  justify-content: center;
}

.vsb-header__nav-list {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.vsb-header__nav-item {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.5rem 0.75rem;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--vsb-navy);
  text-decoration: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
}

.vsb-header__nav-item:hover,
.vsb-header__nav-item--active {
  color: var(--vsb-accent);
  background: var(--vsb-blue-subtle);
}

.vsb-header__contact {
  display: flex;
  gap: 1.25rem;
  flex-shrink: 0;
}

.vsb-header__contact-block {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  text-decoration: none;
  color: inherit;
}

.vsb-header__contact-block:hover strong {
  color: var(--vsb-accent);
}

.vsb-header__contact-block strong {
  display: block;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--vsb-navy);
  line-height: 1.2;
  transition: color 0.2s;
}

.vsb-header__contact-block small {
  display: block;
  font-size: 0.6875rem;
  color: var(--vsb-muted);
  margin-top: 2px;
}

.vsb-header__contact-icon {
  color: var(--vsb-navy);
  margin-top: 2px;
}

.vsb-header__holiday {
  margin: 0;
  padding: 0.35rem 1.25rem 0.65rem;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--vsb-blue-dark);
  background: var(--vsb-blue-light);
}

.vsb-header__action {
  background: var(--vsb-blue-dark);
  color: #fff;
  overflow: visible;
}

.vsb-header__action-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 3.5rem;
}

.vsb-header__categories {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.vsb-header__categories:hover,
.vsb-header__categories--active {
  background: var(--vsb-blue-mid);
}

.vsb-header__categories-icon {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.vsb-header__categories-icon span {
  display: block;
  width: 16px;
  height: 2px;
  background: currentColor;
  border-radius: 1px;
}

.vsb-header__search {
  flex: 1;
}

.vsb-header__cart {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  color: #fff;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.35rem 0.5rem;
  border-radius: 6px;
  position: relative;
}

.vsb-header__cart:hover {
  background: rgba(255, 255, 255, 0.1);
}

.vsb-header__cart-badge {
  position: absolute;
  top: -4px;
  right: -2px;
  min-width: 1.125rem;
  height: 1.125rem;
  padding: 0 4px;
  border-radius: 999px;
  background: #fff;
  color: var(--vsb-blue-dark);
  font-size: 0.625rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vsb-header__burger {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 28px;
  height: 20px;
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.vsb-header__burger span {
  display: block;
  height: 2px;
  background: var(--vsb-navy);
  border-radius: 2px;
}

.show-mobile {
  display: none !important;
}

@media (min-width: 1024px) {
  .vsb-header__utility-note--desktop {
    display: block;
  }
  .vsb-header__announce {
    display: none;
  }
}

@media (max-width: 1023px) {
  .hide-mobile {
    display: none !important;
  }
  .show-mobile {
    display: flex !important;
  }
  .vsb-header__main-row {
    min-height: 3.75rem;
  }
  .vsb-header__action-row {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .vsb-header__cart span:not(.vsb-header__cart-badge) {
    display: none;
  }
}
</style>
